// Event-day jobs (card 540f21a4): what each job can do on a paired device.
// One platform-wide set, set here by admins (Devin 2026-10-23: "I set the
// roles"). Functions down the side, jobs across; a tick saves at once and
// reaches every paired device with that job within a minute. Correcting a
// published result, money, settings, draws and user admin are never jobs'.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import HeaderOrg from '../Headers/HeaderOrg';
import Loader from '../../Components/Loader/Loader';
import { Msr, useMaterialSymbols } from '../OireachtasPanel/symbols';
import { archiveRole, createRole, isConflict, listRoles, updateRole } from './jobRolesApi';
import '../OireachtasPanel/oireachtas.css';
import '../OireachtasPanel/organizerOireachtas.css';
import './jobRoles.css';

const FN_MIN = 260; // the functions column, before the grid scrolls sideways
const JOB_COL = 132; // one job's tick column

const KINDS = [
  { kind: 'feis', label: 'Feis' },
  { kind: 'oireachtas', label: 'Oireachtas' },
];
const messageOf = (err) => err?.messages || err?.message || 'Something went wrong. Please try again.';

// The catalogue's functions for one kind, grouped in catalogue order.
export function groupsOf(catalogue, kind) {
  const out = [];
  for (const f of catalogue.filter((c) => c.kind === kind)) {
    let g = out.find((x) => x.group === f.group);
    if (!g) out.push((g = { group: f.group, functions: [] }));
    g.functions.push(f);
  }
  return out;
}

const JobRoles = () => {
  useMaterialSymbols();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [kind, setKind] = useState('feis');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState('');
  const [renaming, setRenaming] = useState(null); // { id, name }
  const [adding, setAdding] = useState(null); // name being typed
  const [confirmRemove, setConfirmRemove] = useState('');

  const load = useCallback(async () => {
    try {
      setData(await listRoles());
    } catch (e) {
      setError(messageOf(e));
    }
  }, []);
  useEffect(() => {
    load();
  }, [load]);

  const roles = useMemo(() => (data?.roles || []).filter((r) => r.eventKind === kind), [data, kind]);
  const groups = useMemo(() => groupsOf(data?.catalogue || [], kind), [data, kind]);

  const save = async (role, body) => {
    setBusy(role._id);
    setError('');
    try {
      const res = await updateRole(role._id, { ...body, updatedAt: role.updatedAt });
      setData((d) => ({ ...d, roles: d.roles.map((r) => (r._id === role._id ? res.role : r)) }));
      return true;
    } catch (e) {
      setError(isConflict(e) ? 'Someone else changed this job just now — showing the latest.' : messageOf(e));
      await load();
      return false;
    } finally {
      setBusy('');
    }
  };

  const toggle = (role, code) => {
    const has = role.functions.includes(code);
    save(role, { functions: has ? role.functions.filter((c) => c !== code) : [...role.functions, code] });
  };

  const rename = async (e) => {
    e.preventDefault();
    const role = roles.find((r) => r._id === renaming.id);
    if (role && renaming.name.trim() && renaming.name.trim() !== role.name) {
      if (!(await save(role, { name: renaming.name.trim() }))) return;
    }
    setRenaming(null);
  };

  const add = async (e) => {
    e.preventDefault();
    if (!adding || !adding.trim()) return;
    setBusy('add');
    setError('');
    try {
      await createRole({ name: adding.trim(), eventKind: kind, functions: [] });
      setAdding(null);
      await load();
    } catch (err) {
      setError(messageOf(err));
    } finally {
      setBusy('');
    }
  };

  const remove = async (role) => {
    setBusy(role._id);
    setError('');
    try {
      await archiveRole(role._id);
      setConfirmRemove('');
      await load();
    } catch (err) {
      setError(messageOf(err));
    } finally {
      setBusy('');
    }
  };

  // The functions column takes the room left and wraps its descriptions; each
  // job gets one fixed column, so the ticks sit close to their job and every
  // job shows. Only a grid too wide for the page scrolls sideways (Devin
  // 2026-10-30: the columns had spread across the page, Head tabulator cut off).
  const jobs = Math.max(roles.length, 1);
  const cols = { gridTemplateColumns: `minmax(${FN_MIN}px, 1fr) repeat(${jobs}, ${JOB_COL}px)`, minWidth: FN_MIN + jobs * JOB_COL };

  return (
    <>
      <HeaderOrg />
      <div className="oir op-page jr-page">
        {!data && !error && <Loader />}
        <div className="wrap">
          <button type="button" className="op-back" onClick={() => navigate('/')}>
            <Msr>arrow_back</Msr>Dashboard
          </button>
          <div className="ph">
            <div>
              <h1>Event-day jobs</h1>
              <div className="sub">
                What each job can do on a paired laptop or tablet. A change reaches every device with that job within a minute.
              </div>
            </div>
          </div>

          <div className="jr-tabs" role="tablist">
            {KINDS.map((k) => (
              <button key={k.kind} type="button" role="tab" aria-selected={kind === k.kind} className={`op-chip${kind === k.kind ? ' on' : ''}`} onClick={() => setKind(k.kind)}>
                {k.label}
              </button>
            ))}
            {adding === null ? (
              <button type="button" className="op-btn sm jr-add" onClick={() => setAdding('')}>
                <Msr>add</Msr>Add a job
              </button>
            ) : (
              <form className="jr-addform" onSubmit={add}>
                <input autoFocus value={adding} maxLength={40} onChange={(e) => setAdding(e.target.value)} placeholder="Job name" aria-label="New job name" />
                <button type="submit" className="op-btn sm" disabled={!adding.trim() || busy === 'add'}>
                  Add
                </button>
                <button type="button" className="op-btn sm" onClick={() => setAdding(null)}>
                  Cancel
                </button>
              </form>
            )}
          </div>

          {error && (
            <div className="jr-banner" role="alert">
              <Msr>error</Msr>
              {error}
            </div>
          )}

          {data && (
            <div className="jr-scroll">
              <div className="jr-grid" style={cols} role="table" aria-label={`${kind === 'feis' ? 'Feis' : 'Oireachtas'} jobs`}>
                <div className="jr-hcell jr-corner" role="columnheader">
                  Can…
                </div>
                {roles.map((r) => (
                  <div className="jr-hcell" key={r._id} role="columnheader">
                    {renaming?.id === r._id ? (
                      <form onSubmit={rename} className="jr-rename">
                        <input autoFocus value={renaming.name} maxLength={40} onChange={(e) => setRenaming({ ...renaming, name: e.target.value })} aria-label="Job name" onBlur={rename} />
                      </form>
                    ) : (
                      <button type="button" className="jr-name" title="Rename" onClick={() => setRenaming({ id: r._id, name: r.name })}>
                        {r.name}
                      </button>
                    )}
                    <div className="jr-devices">
                      {r.activeDevices ? `${r.activeDevices} device${r.activeDevices === 1 ? '' : 's'}` : 'No devices'}
                    </div>
                    {!r.activeDevices &&
                      (confirmRemove === r._id ? (
                        <span className="jr-remove">
                          <button type="button" className="op-btn sm danger" disabled={busy === r._id} onClick={() => remove(r)}>
                            Remove
                          </button>
                          <button type="button" className="op-btn sm" onClick={() => setConfirmRemove('')}>
                            Keep
                          </button>
                        </span>
                      ) : (
                        <button type="button" className="jr-x" title={`Remove ${r.name}`} aria-label={`Remove ${r.name}`} onClick={() => setConfirmRemove(r._id)}>
                          <Msr>close</Msr>
                        </button>
                      ))}
                  </div>
                ))}
                {groups.map((g) => (
                  <React.Fragment key={g.group}>
                    <div className="jr-group" role="rowheader" style={{ gridColumn: '1 / -1' }}>
                      {g.group}
                    </div>
                    {g.functions.map((f) => (
                      <React.Fragment key={f.code}>
                        <div className="jr-fn" role="rowheader">
                          <div className="jr-fname">{f.label}</div>
                          <div className="jr-fdesc">{f.description}</div>
                        </div>
                        {roles.map((r) => (
                          <label className="jr-cell" key={r._id} role="cell">
                            <input
                              type="checkbox"
                              checked={r.functions.includes(f.code)}
                              disabled={busy === r._id}
                              onChange={() => toggle(r, f.code)}
                              aria-label={`${r.name}: ${f.label}`}
                            />
                            <span className="jr-tick" aria-hidden="true">
                              <Msr>check</Msr>
                            </span>
                          </label>
                        ))}
                      </React.Fragment>
                    ))}
                  </React.Fragment>
                ))}
              </div>
            </div>
          )}
          <p className="jr-note">
            Never part of a job: correcting a published result, money and payouts, event settings, draws, merges and splits, and user
            accounts. Those stay with the organizer’s or an admin’s own sign-in.
          </p>
        </div>
      </div>
    </>
  );
};

export default JobRoles;
